/**
 * Pillola standard per visualizzare una prenotazione in calendari/widget.
 *
 * Formato visualizzato:
 *   N.<camera|?>  <letteraFonte>  <Cognome>  ·  <N>p
 *
 * Lettera fonte con colore:
 *   W arancione · S nero · B blu · A rosso
 *
 * Hover (title attr) → tooltip con dettagli completi.
 */
import { forwardRef } from "react";

/** Resolve source -> {letter, className, label} */
export function resolveSource(rawSource) {
  const s = (rawSource || "").toLowerCase();
  if (s === "walk_in" || s === "walkin" || s === "guide" || s === "guida")
    return { letter: "W", className: "text-orange-600", label: "Walk-in (App Guida)" };
  if (s.includes("airbnb"))
    return { letter: "A", className: "text-rose-600", label: "Airbnb" };
  if (s.includes("booking"))
    return { letter: "B", className: "text-sky-600", label: "Booking.com" };
  // Default: site / direct / manual / empty
  return { letter: "S", className: "text-[#1A1A1A]", label: "Sito / manuale" };
}

/** Compute the surname for display. Accepts string OR {first_name, last_name} OR {guest:{...}}. */
export function resolveSurname(guest) {
  if (!guest) return "—";
  if (typeof guest === "string") {
    // Already a full name string — try to extract the last token as surname
    const cleaned = guest.trim();
    if (!cleaned) return "—";
    // If contains comma: "Last, First" → take part before comma
    if (cleaned.includes(",")) return cleaned.split(",")[0].trim();
    // Otherwise take everything after the first space, or the whole string
    const bits = cleaned.split(/\s+/);
    if (bits.length === 1) return bits[0];
    return bits.slice(1).join(" ");
  }
  // Object shape
  return (guest.last_name || guest.first_name || "—").toString();
}

/**
 * `booking` may be either the rich Booking object (`{guest:{first_name,last_name}, ...}`)
 *  or the lighter dashboard event (`{guest_name, room_name, guide_room_number, ...}`).
 *  We try to normalise both.
 */
function normalise(booking) {
  const guest =
    booking.guest ||
    booking.guest_name ||
    `${booking.first_name || ""} ${booking.last_name || ""}`.trim() ||
    null;
  return {
    surname: resolveSurname(guest),
    guests: booking.guests || 1,
    roomNum: booking.guide_room_number ?? null,
    roomName: booking.room_name || "",
    source: booking.source || "",
    checkIn: booking.check_in || "",
    checkOut: booking.check_out || "",
    code: booking.code || "",
    total: booking.total != null ? Number(booking.total) : null,
  };
}

const BookingPill = forwardRef(function BookingPill(
  { booking, compact = false, className = "", title, onClick, "data-testid": testId, ...rest },
  ref,
) {
  const n = normalise(booking);
  const src = resolveSource(n.source);
  // iter188 (corretto): stesso comportamento del calendario elenco
  // prenotazioni — se `guide_room_number` è assegnato mostra "n<num>",
  // altrimenti "n?". Nessun fallback su nome camera/cognome.
  const roomLabel = n.roomNum != null && n.roomNum !== ""
    ? `n${n.roomNum}`
    : "n?";
  const fullName =
    typeof booking.guest === "object" && booking.guest
      ? `${booking.guest.first_name || ""} ${booking.guest.last_name || ""}`.trim()
      : typeof booking.guest === "string"
        ? booking.guest
        : booking.guest_name || "";
  const computedTitle =
    title ||
    [
      `${roomLabel} · ${src.label}`,
      fullName ? `Ospite: ${fullName}` : null,
      `${n.guests} ospit${n.guests > 1 ? "i" : "e"}`,
      n.checkIn && n.checkOut ? `${n.checkIn} → ${n.checkOut}` : null,
      n.roomName ? `Camera: ${n.roomName}` : null,
      n.total != null && n.total > 0 ? `Totale: €${n.total.toFixed(2)}` : null,
      n.code ? `Codice: ${n.code}` : null,
    ]
      .filter(Boolean)
      .join(" · ");

  return (
    <span
      ref={ref}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      title={computedTitle}
      data-testid={testId || "booking-pill"}
      className={`block whitespace-nowrap ${compact ? "text-[10px] sm:text-[11px]" : "text-[12px] overflow-hidden text-ellipsis"} ${className}`}
      {...rest}
    >
      {/* iter188 — Room label sempre visibile (anche mobile) con font
          leggermente più grande + colore fonte inline così a colpo d'occhio
          si legge SIA il numero camera SIA da quale canale arriva.
          In modalità compact il testo è forzato a NON troncarsi così le
          3 lettere restano leggibili anche in celle strette. */}
      <span className={`font-mono font-bold tabular-nums ${src.className}`}>{roomLabel}</span>
      <span className="hidden sm:inline"> </span>
      <span className={`font-extrabold hidden sm:inline ${src.className}`} aria-label={src.label}>
        {src.letter}
      </span>
      <span className="hidden sm:inline"> </span>
      <span className="font-semibold hidden sm:inline">{n.surname}</span>
      <span className="opacity-60 hidden sm:inline"> · </span>
      <span className="font-semibold hidden sm:inline">{n.guests}p</span>
    </span>
  );
});

export default BookingPill;
